"use client";

import { Mail, MapPin, Phone, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const productOptions = ["Network Infrastructure", "Video Surveillance", "Access Control", "VoIP Phone Systems", "Integrated Systems"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[var(--border)] bg-[var(--card)]/40 backdrop-blur-md">
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-[var(--primary)] mb-4">
          / TRANSMISSION RECEIVED
        </p>
        <p className="font-serif text-lg text-foreground mb-6 leading-relaxed">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono uppercase tracking-[0.15em] text-xs border-[var(--border)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default font-bold text-[13px] uppercase tracking-[0.15em] text-foreground">
              Full Name <span className="text-[var(--primary)]">*</span>
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="h-12 rounded-md border-0 border-b border-[var(--border)] bg-transparent px-0 font-sans text-base focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:[box-shadow:0_1px_0_0_var(--primary)] transition-all placeholder:text-[var(--muted-foreground)]" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="company_name" rules={{
      required: "Company is required"
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default font-bold text-[13px] uppercase tracking-[0.15em] text-foreground">
              Business Name <span className="text-[var(--primary)]">*</span>
            </FormLabel>
            <FormControl>
              <Input placeholder="Your Company" {...field} className="h-12 rounded-md border-0 border-b border-[var(--border)] bg-transparent px-0 font-sans text-base focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:[box-shadow:0_1px_0_0_var(--primary)] transition-all placeholder:text-[var(--muted-foreground)]" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default font-bold text-[13px] uppercase tracking-[0.15em] text-foreground">
                Email <span className="text-[var(--primary)]">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" {...field} className="h-12 rounded-md border-0 border-b border-[var(--border)] bg-transparent px-0 font-sans text-base focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:[box-shadow:0_1px_0_0_var(--primary)] transition-all placeholder:text-[var(--muted-foreground)]" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone is required",
        pattern: {
          value: /^[+\d\s()-]{7,}$/,
          message: "Invalid phone"
        }
      }} render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default font-bold text-[13px] uppercase tracking-[0.15em] text-foreground">
                Phone <span className="text-[var(--primary)]">*</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="h-12 rounded-md border-0 border-b border-[var(--border)] bg-transparent px-0 font-sans text-base focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:[box-shadow:0_1px_0_0_var(--primary)] transition-all placeholder:text-[var(--muted-foreground)]" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="product_interest" rules={{
      required: "Please select a solution"
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default font-bold text-[13px] uppercase tracking-[0.15em] text-foreground">
              Solution Interest <span className="text-[var(--primary)]">*</span>
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <SelectTrigger className="h-12 w-full rounded-md border-0 border-b border-[var(--border)] bg-transparent px-0 font-sans text-base focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:[box-shadow:0_1px_0_0_var(--primary)] data-[placeholder]:text-[var(--muted-foreground)]">
                  <SelectValue placeholder="Select a solution" />
                </SelectTrigger>
                <SelectContent className="rounded-md border border-[var(--border)] bg-[var(--popover)]">
                  {productOptions.map((option, i) => <SelectItem key={option} value={option} data-index={i} className="font-sans text-base focus:bg-[var(--accent)] focus:text-[var(--accent-foreground)]">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 20,
        message: "Min 20 characters"
      },
      maxLength: {
        value: 1000,
        message: "Max 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default font-bold text-[13px] uppercase tracking-[0.15em] text-foreground">
              Project Details <span className="text-[var(--primary)]">*</span>
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your IT infrastructure needs." {...field} rows={5} className="min-h-[140px] rounded-md border-0 border-b border-[var(--border)] bg-transparent px-0 font-sans text-base resize-none focus-visible:ring-0 focus-visible:border-[var(--primary)] focus-visible:[box-shadow:0_1px_0_0_var(--primary)] transition-all placeholder:text-[var(--muted-foreground)]" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-[var(--destructive)] bg-[var(--destructive)]/10">
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-[var(--destructive)]">
            {submitError}
          </p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-[var(--primary)] text-[var(--primary-foreground)] font-default font-bold uppercase tracking-[0.15em] text-sm shadow-2xl hover:bg-[var(--primary)]/90 hover:shadow-[0_0_40px_var(--primary)] transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Transmitting...
            </> : <>Send Inquiry →</>}
        </Button>
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted-foreground)] leading-relaxed">
          / Your data is secured via enterprise-grade encryption. We will respond within 24 hours.
        </p>
      </div>
    </Form>;
}
export default function Connect() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3641" id="connect-with-us" className="bg-background text-foreground relative overflow-hidden py-32 md:py-[200px] px-6 md:px-12 lg:px-20">
      <div className="absolute top-1/4 -right-40 w-[600px] h-[600px] rounded-lg opacity-[0.07] blur-3xl pointer-events-none" style={{
      background: "var(--primary)"
    }} aria-hidden="true" />

      <div className="max-w-[1400px] mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-16 lg:gap-20">
          <div ref={leftRef} className={`flex flex-col transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--primary)] mb-12">
              01 / CONTACT
            </p>

            <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tighter text-foreground mb-10">
              <span className="block text-[clamp(4rem,9vw,7rem)]">UNIFIED</span>
              <span className="block text-[clamp(4rem,9vw,7rem)] ml-[0.15em]">SOLUTIONS</span>
              <span className="block text-[clamp(4rem,9vw,7rem)] ml-[0.3em]">
                FOR YOU<span className="text-[var(--primary)]">.</span>
              </span>
            </h2>

            <p className="font-sans text-lg leading-relaxed text-[var(--muted-foreground)] max-w-md mb-12">
              Betaquest, LLC specializes in unified IT solutions tailored to your business needs. Performance, security, and scalability integrated.
            </p>

            <div className="space-y-6 mt-auto">
              <Link to="tel:+15550000000" className="group flex items-start gap-4 border-t border-[var(--border)] pt-6">
                <Phone className="size-4 mt-1 stroke-[1] text-[var(--muted-foreground)] group-hover:text-[var(--primary)] transition-colors" aria-hidden="true" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted-foreground)] mb-2">
                    / TELEPHONE
                  </p>
                  <p className="font-default text-base text-foreground group-hover:text-[var(--primary)] transition-colors relative inline-block">+1 (800) 888-0969<span className="absolute left-0 -bottom-0.5 w-0 h-px bg-[var(--primary)] group-hover:w-full transition-all duration-300" data-ast-id="f:/src/pages/Home/sections/Connect.tsx|sig:egqkur" data-ast-start="11665" data-ast-end="11902"></span></p>
                </div>
              </Link>

              <Link to="mailto:info@betaquest.com" className="group flex items-start gap-4 border-t border-[var(--border)] pt-6">
                <Mail className="size-4 mt-1 stroke-[1] text-[var(--muted-foreground)] group-hover:text-[var(--primary)] transition-colors" aria-hidden="true" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted-foreground)] mb-2">
                    / EMAIL
                  </p>
                  <p className="font-default text-base text-foreground group-hover:text-[var(--primary)] transition-colors relative inline-block">
                    info@betaquest.com
                    <span className="absolute left-0 -bottom-0.5 w-0 h-px bg-[var(--primary)] group-hover:w-full transition-all duration-300" />
                  </p>
                </div>
              </Link>

              <div className="group flex items-start gap-4 border-t border-b border-[var(--border)] py-6">
                <MapPin className="size-4 mt-1 stroke-[1] text-[var(--muted-foreground)]" aria-hidden="true" />
                <div>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted-foreground)] mb-2">
                    / HEADQUARTERS
                  </p>
                  <p className="font-default text-base text-foreground leading-relaxed">Johns Creek, GA 30022<br /></p>
                </div>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`relative transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="connect_with_us_form_home" formId="connect_with_us_form_home" className="relative border border-[var(--border)] bg-[var(--card)]/30 backdrop-blur-xl p-8 md:p-12 lg:p-14 shadow-4xl" sectionName="connect-with-us" defaultValues={{
            name: "",
            company_name: "",
            email: "",
            phone: "",
            product_interest: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            company_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            product_interest: {
              required: true
            },
            message: {
              required: true,
              minLength: 20,
              maxLength: 1000
            }
          }} submitText="Send Inquiry" successMessage="Inquiry received. Our team will be in touch within 24 hours." errorMessage="Transmission failed. Please verify your details and try again.">
              <div className="mb-10 pb-6 border-b border-[var(--border)]">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--primary)] mb-3">
                  / INQUIRY FORM
                </p>
                <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight text-foreground">
                  Start Your Project
                </h3>
              </div>
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}